import React, { useState } from "react";
import styled from "styled-components";
import { db, storage } from "./firebase/firebaseConfig";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { ArrowLeftCircle, Upload, Youtube } from "lucide-react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const Container = styled.div`
  min-height: 100vh;
  background: #121212;
  color: #fff;
  padding: 40px;
  font-family: "Roboto", sans-serif;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 20px;
  background: linear-gradient(90deg, #00d4ff, #00bcd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Form = styled.form`
  max-width: 800px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  padding: 30px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #fff;
  font-size: 1rem;
  &:focus {
    border-color: #00d4ff;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #fff;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  &:focus {
    border-color: #00d4ff;
    outline: none;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #fff;
  &:focus {
    border-color: #00d4ff;
    outline: none;
  }
`;

const UploadBox = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 2px dashed #444;
  border-radius: 10px;
  padding: 14px;
  cursor: pointer;
  color: #aaa;
  justify-content: center;
  transition: 0.3s;
  &:hover {
    border-color: #00d4ff;
    color: #00d4ff;
  }
`;

const ThumbnailPreview = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 10px;
  margin-top: 10px;
  object-fit: cover;
  border: 2px solid #00d4ff;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #000;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  width: fit-content;
  &:hover {
    transform: translateY(-2px);
  }
`;

const BackButton = styled(Button)`
  background: transparent;
  color: #00d4ff;
  border: 1px solid #00d4ff;
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  color: #00d4ff;
  font-size: 1.2rem;
`;

const AddReadyCode = () => {
  const [codeData, setCodeData] = useState({
    title: "",
    description: "",
    category: "",
    code: "",
    videoURL: "",
  });
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCodeData({ ...codeData, [e.target.name]: e.target.value });
  };

  // ✅ Generate SEO-friendly slug
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeData.title || !codeData.category) {
      alert("⚠️ Please fill all required fields!");
      return;
    }

    try {
      setUploading(true);
      let fileURL = "";
      let thumbnailURL = "";

      // ✅ Upload thumbnail if provided
      if (thumbnail) {
        const thumbRef = ref(
          storage,
          `readyCodes/thumbnails/${Date.now()}_${thumbnail.name}`
        );
        await uploadBytes(thumbRef, thumbnail);
        thumbnailURL = await getDownloadURL(thumbRef);
      }

      // ✅ Upload code file if provided
      if (file) {
        const fileRef = ref(storage, `readyCodes/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        fileURL = await getDownloadURL(fileRef);
      }

      // ✅ Generate unique slug
      let slug = generateSlug(codeData.title);
      const q = query(collection(db, "readyCodes"), where("slug", "==", slug));
      const existing = await getDocs(q);
      if (!existing.empty) slug = `${slug}-${Date.now()}`;

      // ✅ Save everything
      await addDoc(collection(db, "readyCodes"), {
        ...codeData,
        slug,
        fileURL,
        thumbnailURL,
        downloads: 0,
        createdAt: serverTimestamp(),
      });

      alert("✅ Ready-to-Use Code added successfully!");
      navigate("/admin");
    } catch (err) {
      console.error("❌ Error adding code:", err);
      alert("Error adding code");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container>
      {uploading && (
        <LoaderOverlay>🚀 Uploading your code... Please wait</LoaderOverlay>
      )}

      <BackButton onClick={() => navigate(-1)}>
        <ArrowLeftCircle size={18} /> Go Back
      </BackButton>
      <Title>Add Ready-to-Use Code</Title>

      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          name="title"
          placeholder="Code Title"
          value={codeData.title}
          onChange={handleChange}
          required
        />

        <TextArea
          name="description"
          placeholder="Short Description"
          value={codeData.description}
          onChange={handleChange}
        />

        <Select
          name="category"
          value={codeData.category}
          onChange={handleChange}
          required
        >
          <option value="">Select Category</option>
          <option value="React">React</option>
          <option value="React Native">React Native</option>
          <option value="Firebase">Firebase</option>
          <option value="UI Components">UI Components</option>
          <option value="Animations">Animations</option>
          <option value="Backend">Backend</option>
        </Select>

        {/* 🎥 YouTube Video URL */}
        <Input
          type="url"
          name="videoURL"
          placeholder="YouTube Video URL (optional)"
          value={codeData.videoURL}
          onChange={handleChange}
        />

        {/* 🖼 Thumbnail Upload */}
        <div>
          <UploadBox>
            <Upload size={20} />
            <span>{thumbnail ? thumbnail.name : "Upload Thumbnail Image"}</span>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => setThumbnail(e.target.files[0])}
            />
          </UploadBox>
          {thumbnail && (
            <ThumbnailPreview
              src={URL.createObjectURL(thumbnail)}
              alt="Thumbnail Preview"
            />
          )}
        </div>

        {/* 📦 File Upload */}
        <UploadBox>
          <Upload size={20} />
          <span>{file ? file.name : "Upload ZIP or JS file"}</span>
          <input
            type="file"
            accept=".zip,.js,.jsx,.ts,.tsx"
            style={{ display: "none" }}
            onChange={(e) => setFile(e.target.files[0])}
          />
        </UploadBox>

        {/* 💻 Code Editor */}
        <div>
          <p
            style={{ marginBottom: "8px", color: "#00d4ff", fontWeight: "600" }}
          >
            Write Code (Optional)
          </p>
          <CKEditor
            editor={ClassicEditor}
            data={codeData.code}
            onChange={(event, editor) => {
              const data = editor.getData();
              setCodeData({ ...codeData, code: data });
            }}
          />
        </div>

        <Button type="submit" disabled={uploading}>
          {uploading ? "Uploading..." : "Add Ready Code"}
        </Button>
      </Form>
    </Container>
  );
};

export default AddReadyCode;
